import { ulid, isConflict, NON_RETRYABLE, type ErrorCode } from '@delivery/shared';
import { ApiFailure, api } from './api';
import { idb } from './idb';
import { errorText, reportClientError } from './report';

/**
 * 离线操作队列。
 *
 * 所有写操作先进本地队列,再异步同步。司机在地下车库、电梯、郊区
 * 经常没信号 —— **离线不可用的配送 App 等于不可用**。
 * 见 docs/05-driver-app.md §05.6
 */

export type ItemStatus = 'QUEUED' | 'SYNCING' | 'CONFLICT' | 'DEAD';

export interface OutboxItem {
  /** 本地生成,同时作为 idempotencyKey —— 重传不会产生重复事件 */
  id: string;
  createdAt: number;
  /** 按订单串行化的分组键。批量操作用第一个订单号 */
  orderId: string;
  /** 涉及的全部订单(批量取货时多个) */
  orderIds: string[];
  action: string;
  endpoint: string;
  method: 'POST' | 'PATCH';
  body: Record<string, unknown>;

  /**
   * 待上传的照片。离线时先存 Blob,同步时才传对象存储 ——
   * 拿到 storageKey 后按 slot 回填进 body。
   *
   * ★ `storageKey` 一旦拿到就**写回队列项**,下次重试不再重传那一张。
   *
   *   原来没有这个字段,于是每一次重试都把照片从头传一遍 ——
   *   而重试发生的时候恰恰是网不好的时候:最贵的那一步被反复重做,
   *   一张都没少传,提交却还是超时。队列越卡越死,
   *   司机看到的是一排「还在发送:送达、送达、送达…」怎么也不走,
   *   而他的流量在一直跑。
   */
  photos: StoredPhoto[];

  attempts: number;
  nextRetryAt: number;
  status: ItemStatus;
  error?: { code: string; message: string };
}

/**
 * 队列里的一张照片。
 *
 * ★ 存的是**字节**(ArrayBuffer),不是 Blob。
 *
 *   iOS Safari 往 IndexedDB 里存 Blob 时会随机报
 *   「Error preparing Blob/File data to be stored in object store」——
 *   WebKit 的老毛病,手机存储紧、页面在后台挂久了之后更容易撞上。
 *   一撞上入队就失败,司机连「确认取货」都点不成(用户发来的截图就是这句)。
 *   ArrayBuffer 是普通的结构化数据,不走 Blob 那条路,存得进去。
 *
 *   `blob` 只为读出升级前就排在队列里的旧条目,新条目不再写它。
 */
export interface StoredPhoto {
  slot: string;
  purpose: string;
  data?: ArrayBuffer;
  type?: string;
  blob?: Blob;
  storageKey?: string;
}

/** 上传时把字节还原成 Blob;旧条目直接用它存着的 Blob */
function photoBlob(p: StoredPhoto): Blob {
  if (p.data) return new Blob([p.data], { type: p.type || 'image/jpeg' });
  if (p.blob) return p.blob;
  throw new Error('照片数据丢失');
}

type Listener = (items: OutboxItem[]) => void;
const listeners = new Set<Listener>();

/**
 * 当前进行中的同步。
 *
 * 用 promise 而不是 boolean 标志:调用方 `await sync()` 时应该等到
 * **这一轮同步真的结束**,而不是发现有人在跑就立即返回 ——
 * 后者会让「入队后立刻检查结果」这种再自然不过的用法拿到过期状态。
 */
let inflight: Promise<void> | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

async function notify(): Promise<void> {
  const items = await idb.all<OutboxItem>();
  items.sort((a, b) => a.createdAt - b.createdAt);
  listeners.forEach((l) => l(items));
}

export const outbox = {
  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    void notify();
    return () => listeners.delete(fn);
  },

  async list(): Promise<OutboxItem[]> {
    const items = await idb.all<OutboxItem>();
    return items.sort((a, b) => a.createdAt - b.createdAt);
  },

  /** 入队并立即尝试同步。返回本地 id,调用方用它做乐观 UI */
  async enqueue(input: {
    orderIds: string[];
    action: string;
    endpoint: string;
    method?: 'POST' | 'PATCH';
    body: Record<string, unknown>;
    photos?: Array<{ slot: string; purpose: string; blob: Blob }>;
  }): Promise<string> {
    const item: OutboxItem = {
      id: ulid(),
      createdAt: Date.now(),
      orderId: input.orderIds[0] ?? 'none',
      orderIds: input.orderIds,
      action: input.action,
      endpoint: input.endpoint,
      method: input.method ?? 'POST',
      body: input.body,
      photos: await Promise.all(
        (input.photos ?? []).map(async (p) => ({
          slot: p.slot,
          purpose: p.purpose,
          data: await p.blob.arrayBuffer(),
          type: p.blob.type || 'image/jpeg',
        })),
      ),
      attempts: 0,
      nextRetryAt: 0,
      status: 'QUEUED',
    };
    try {
      await idb.put(item);
    } catch (e) {
      // ★ 存不进本地是 iOS Safari 上见过的事(Blob 那一回)—— 报上去,好知道还有没有别的花样
      reportClientError({
        kind: 'outbox',
        reason: 'put',
        message: errorText(e),
        details: [`action=${item.action} photos=${item.photos.length}`],
      });
      throw e;
    }
    await notify();
    // 不阻塞调用方 —— 司机点完就能走。要等结果的话再 await outbox.sync()
    void outbox.sync();
    return item.id;
  },

  /**
   * 等这一条的**第一次同步结果**。
   *
   * @returns `null` 表示已经同步成功(队列里没有了);否则返回这一条,
   *          `error` 里是被拒绝的原因。
   *
   * ★ 队列的设计是「点完就能走」—— 那是为了地下车库和电梯。
   *   但司机**站在店里**点确认取货的时候,他是在等一个结果:
   *   成了就走人,没成他得当场处理(开定位、走近一点、找店员)。
   *   这时候把失败咽下去,他会以为成了 —— 而订单一直没动。
   *
   * ★ 离线时直接返回 `null`,不等 —— 那正是队列存在的意义,
   *   排上队就是这一步能给的最好结果,不该报错吓他。
   */
  /**
   * 等这一条**真的被发过一次**,然后把它现在的样子交出去。
   *
   * ★ 回 null = 没这条了(发成功了,或者离线还没试)。
   *   回一条带 error 的 = 试过、失败了,调用方要当场告诉司机。
   *   回一条**没有 error 的** = 还在队列里(见下面为什么会这样)。
   *
   * ★ 这里必须**可能跑两趟** sync。
   *
   *   `sync()` 里有一句「已有同步在跑就复用它」——而那一趟是在这一条
   *   入队**之前**开始的,它的 items 快照里没有这一条。只跑一趟的话,
   *   `settle` 读回来的是一条仍然 QUEUED、**没有 error** 的记录,
   *   而调用方(取货、批量送达)会把「没有 error」当成成功:
   *   界面刷一下、什么都没变 —— 司机站在店里点了一下,毫无反应,
   *   也没有任何一句话说明为什么。30 秒后的定时兜底才把它发出去,
   *   所以他的体验是「刷新一下再点就好了」。(用户报的就是这个。)
   *
   *   所以:第一趟回来之后,如果这一条**一次都没被试过**
   *   (还在、QUEUED、attempts 为 0),再跑一趟 —— 那时 inflight 已经清空,
   *   这一趟一定会看到它。
   */
  async settle(id: string): Promise<OutboxItem | null> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return null;
    await outbox.sync();
    let item = await idb.get<OutboxItem>(id);
    if (item && item.status === 'QUEUED' && item.attempts === 0) {
      await outbox.sync();
      item = await idb.get<OutboxItem>(id);
    }
    return item ?? null;
  },

  /** 丢弃一条(冲突后司机确认知情,或死信手动清理) */
  async discard(id: string): Promise<void> {
    await idb.delete(id);
    await notify();
  },

  async retry(id: string): Promise<void> {
    const item = await idb.get<OutboxItem>(id);
    if (!item) return;
    await idb.put({ ...item, status: 'QUEUED', attempts: 0, nextRetryAt: 0, error: undefined });
    await notify();
    void outbox.sync();
  },

  /**
   * 同步。
   *
   * ★ 同一订单的操作**严格串行** —— 取货必须在送达之前到达服务器。
   *   不同订单可以并行,但这里为简单起见整体串行(司机端量级很小)。
   */
  sync(): Promise<void> {
    // navigator.onLine 在移动端不可靠(连上 WiFi 但没外网也报 true),
    // 原生下由 watchNetwork 推送的状态更准 —— 这里只做粗过滤
    if (!navigator.onLine) return Promise.resolve();
    // 已有同步在跑就复用它 —— 调用方能正确等到结果
    if (inflight) return inflight;

    inflight = (async () => {
      try {
        const items = await outbox.list();
        const now = Date.now();
        const seenOrders = new Set<string>();

        for (const item of items) {
          // 冲突要留着给司机看那张卡(货可能还在他手上),不自动处理
          if (item.status === 'CONFLICT') continue;
          /*
           * ★ 放弃掉的那些:先**上报**,报成了才删。
           *
           *   司机端已经不为这件事弹任何东西了,所以它必须有个去处 ——
           *   一条「送达」就这么没了而没人知道,是这套系统里最坏的一种
           *   静默失败。报到订单时间轴上,调度看得见。
           */
          if (item.status === 'DEAD') {
            await abandon(item);
            continue;
          }
          if (item.nextRetryAt > now) continue;
          // 同一订单前面还有待同步的,先不处理后面的
          if (seenOrders.has(item.orderId)) continue;
          seenOrders.add(item.orderId);

          await syncOne(item);
        }
      } finally {
        inflight = null;
        await notify();
      }
    })();

    return inflight;
  },

  /** 启动自动同步:网络恢复 / 回到前台 / 定时兜底 */
  start(): () => void {
    const kick = () => void outbox.sync();

    window.addEventListener('online', kick);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') kick();
    });
    timer = setInterval(kick, 30_000);
    kick();

    return () => {
      window.removeEventListener('online', kick);
      if (timer) clearInterval(timer);
    };
  },
};

/**
 * 上报并丢弃一条**永远发不出去**的操作。
 *
 * ★ 报不上去就**不删**。删了的那一下再也没人知道,而报不上去多半就是
 *   因为没网 —— 而没网正是它发不出去的原因。留着,下一轮再报。
 *
 * ★ 批量操作要按**每一单**报。一次取货批里三十单,只报第一单的话,
 *   调度在别的二十九单上看不到任何痕迹。
 *
 * ★ 出错不往外抛:这是收尾动作,不能让它把整轮同步打断 ——
 *   后面还排着能发出去的单。
 */
async function abandon(item: OutboxItem): Promise<void> {
  const orderIds = item.orderIds.length > 0 ? item.orderIds : [item.orderId];
  try {
    for (const orderId of orderIds) {
      await api(`/orders/${orderId}/sync-abandoned`, {
        method: 'POST',
        body: {
          action: item.action,
          attempts: item.attempts,
          ...(item.error ? { error: item.error } : {}),
        },
      });
    }
    await idb.delete(item.id);
  } catch {
    /* 报不上去就留着,下一轮再来 —— 不能悄悄删掉 */
  }
}

async function syncOne(input: OutboxItem): Promise<void> {
  /*
   * ★ 本地这一份也跟着变成 SYNCING,而不只是写库。
   *   下面传照片时要拿它往回写(把 storageKey 记住),
   *   拿一份状态过期的去写,会把刚标上的 SYNCING 又抹回去。
   */
  let item: OutboxItem = { ...input, status: 'SYNCING' as ItemStatus };
  await idb.put(item);

  try {
    const body = { ...item.body };

    // ★ 用队列项的本地 id 作为幂等键 —— 重传(网络抖动/进程重启)
    //   不会产生重复事件。调用方留空字段,这里统一覆盖
    if ('idempotencyKey' in body) body['idempotencyKey'] = item.id;
    if ('batchKey' in body) body['batchKey'] = item.id;

    // 先把离线暂存的照片传上去,拿 storageKey 回填。
    // 已经传成功的那几张会被跳过 —— 见 uploadPending 的说明
    if (item.photos.length > 0) {
      item = await uploadPending(item);
      applyPhotoKeys(
        body,
        item.photos
          .filter((p) => p.storageKey)
          .map((p) => ({ slot: p.slot, storageKey: p.storageKey! })),
      );
    }

    const res = await api<unknown>(item.endpoint, { method: item.method, body });

    /**
     * ★ 批量接口**部分失败也返回 200**,逐单结果在响应体里。
     *
     * 只看 HTTP 状态码的话,「订单已被取消,取货失败」这种结果会被
     * 当成成功、直接删掉队列项 —— 司机以为货取到了,系统里那单其实没动。
     * 而取货走的恰恰就是批量接口,等于整条离线取货链路没有失败反馈。
     */
    const failed = batchFailure(res);
    if (failed) {
      throw new ApiFailure(409, {
        code: failed.code,
        message: failed.message,
        ...(failed.details ? { details: failed.details } : {}),
      });
    }

    await idb.delete(item.id);
  } catch (e) {
    const failure = e instanceof ApiFailure ? e : null;
    const code = (failure?.code ?? 'NETWORK_ERROR') as ErrorCode;

    // 冲突:不是重试能解决的。拉最新状态覆盖本地并明确告知司机
    if (failure && isConflict(code)) {
      await idb.put({
        ...item,
        status: 'CONFLICT' as ItemStatus,
        error: { code, message: failure.message },
      });
      return;
    }

    // 校验类错误重试也没用,直接进死信
    if (failure && (NON_RETRYABLE as readonly string[]).includes(code)) {
      await idb.put({
        ...item,
        status: 'DEAD' as ItemStatus,
        error: { code, message: failure.message },
      });
      return;
    }

    const attempts = item.attempts + 1;
    const dead = attempts >= 10;
    await idb.put({
      ...item,
      status: (dead ? 'DEAD' : 'QUEUED') as ItemStatus,
      attempts,
      // 指数退避,封顶 5 分钟
      nextRetryAt: Date.now() + Math.min(2 ** attempts * 5000, 300_000),
      error: { code, message: failure?.message ?? '网络异常' },
    });
  }
}

/**
 * 从批量接口的响应里挑出第一条失败。
 *
 * 只取第一条:司机端一次批量就是一个取货点的几单,失败原因通常相同,
 * 逐条展示反而看不清。真要逐单看,管理端有完整明细。
 */
function batchFailure(
  res: unknown,
): { code: string; message: string; details?: Record<string, unknown> } | null {
  if (!res || typeof res !== 'object') return null;
  const results = (res as { results?: unknown }).results;
  if (!Array.isArray(results)) return null;

  for (const r of results) {
    if (r && typeof r === 'object' && (r as { status?: string }).status === 'FAILED') {
      const err = (r as { error?: { code?: string; message?: string; details?: unknown } }).error;
      return {
        code: err?.code ?? 'INTERNAL_ERROR',
        message: err?.message ?? '操作失败',
        ...(err?.details && typeof err.details === 'object'
          ? { details: err.details as Record<string, unknown> }
          : {}),
      };
    }
  }
  return null;
}

interface Presigned {
  uploadUrl: string;
  storageKey: string;
  requiredHeaders: Record<string, string>;
}

/**
 * 把还没传上去的照片传掉,**传一张记一张**。
 *
 * ★ 跳过已经有 `storageKey` 的。重试时最不该重做的就是这一步:
 *   一张送达照片几百 KB 到几 MB,而重试发生的时候正是网不好的时候。
 *   原来每次重试都从第一张重传,于是队列在弱网下根本走不完 ——
 *   司机看到的是一排「还在发送」不动,流量却在一直跑。
 *
 * ★ 每传完一张就写回队列项,不是等全部传完再写。
 *   三张照片传到第二张时断了的话,已经上去的那一张下次不用再传;
 *   等全部传完才写的话,那一张等于白传。
 *
 * ★ 失败照旧往外抛 —— 整条操作留在队列里重试(这是原来就有的行为,
 *   不能改:照片是「货确实交出去了」的唯一凭证,丢了就说不清了)。
 */
async function uploadPending(item: OutboxItem): Promise<OutboxItem> {
  let cur = item;
  for (let i = 0; i < cur.photos.length; i++) {
    const p = cur.photos[i]!;
    if (p.storageKey) continue;

    const blob = photoBlob(p);
    const pre = await api<Presigned>('/uploads/presign', {
      method: 'POST',
      body: { purpose: p.purpose, contentType: 'image/jpeg', contentLength: blob.size },
    });
    const res = await fetch(pre.uploadUrl, {
      method: 'PUT',
      headers: pre.requiredHeaders,
      body: blob,
    });
    if (!res.ok) throw new Error(`照片上传失败 (${res.status})`);

    cur = {
      ...cur,
      photos: cur.photos.map((q, j) => (j === i ? { ...q, storageKey: pre.storageKey } : q)),
    };
    await idb.put(cur);
  }
  return cur;
}

/**
 * 把上传得到的 storageKey 填回请求体。
 *
 * slot 格式:
 *   `delivery`            → payload.deliveryProofs
 *   `customer:{id}`       → payload.proofsByCustomer[id]
 *   `fail`                → payload.deliveryProofs(FAIL_PHOTO 类型)
 *
 * ★ 这里写的 `type` 会**覆盖**后端的默认值(insertProofs 用 `p.type || defaultType`)。
 *   所以必须和上传 purpose、后端默认值用同一套词表 —— 曾经这里写死了泛型的
 *   `PHOTO`,后端默认改成 POD_PHOTO 也没用,管理端一直显示原始码值
 */
function applyPhotoKeys(
  body: Record<string, unknown>,
  uploaded: Array<{ slot: string; storageKey: string }>,
): void {
  const payload = (body['payload'] ??= {}) as Record<string, unknown>;

  for (const u of uploaded) {
    if (u.slot === 'delivery') {
      const arr = (payload['deliveryProofs'] ??= []) as Array<Record<string, string>>;
      arr.push({ type: 'POD_PHOTO', storageKey: u.storageKey });
    } else if (u.slot === 'fail') {
      const arr = (payload['deliveryProofs'] ??= []) as Array<Record<string, string>>;
      arr.push({ type: 'FAIL_PHOTO', storageKey: u.storageKey });
    } else if (u.slot.startsWith('customer:')) {
      const customerId = u.slot.slice('customer:'.length);
      const map = (payload['proofsByCustomer'] ??= {}) as Record<
        string,
        Array<Record<string, string>>
      >;
      (map[customerId] ??= []).push({ type: 'PICKUP_PHOTO', storageKey: u.storageKey });
    }
  }
}
